/**
 * Full-bleed animated vector background for the current theme. All the work
 * happens in BackgroundEngine (engine.ts); this component just owns the
 * <canvas> and forwards prop changes.
 */
import { useEffect, useRef } from 'react';
import type { ThemePack } from '@dropstack/core';
import { BackgroundEngine } from './engine.ts';

export interface BackgroundCanvasProps {
  theme: ThemePack;
  reducedMotion: boolean;
  /** Freeze animation (game paused / tab hidden). */
  paused?: boolean;
  /** 0–1 tension (stack height) — may tint/quicken ambient motion very slightly. */
  intensity?: number;
  className?: string;
}

const CANVAS_STYLE = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  display: 'block',
  pointerEvents: 'none',
} as const;

/** Full-bleed animated vector background. Crossfades when `theme` changes. */
export function BackgroundCanvas({ theme, reducedMotion, paused = false, intensity = 0, className }: BackgroundCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<BackgroundEngine | null>(null);
  // Initial options for the engine, read once on mount (later changes go through setOptions).
  const initial = useRef({ reducedMotion, paused, intensity });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const engine = new BackgroundEngine(canvas, initial.current);
    engineRef.current = engine;
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    engineRef.current?.setOptions({ reducedMotion, paused, intensity });
  }, [reducedMotion, paused, intensity]);

  useEffect(() => {
    engineRef.current?.setTheme(theme);
  }, [theme]);

  return <canvas ref={canvasRef} className={className} style={CANVAS_STYLE} aria-hidden="true" data-theme-id={theme.id} />;
}
